import React, { useState, useRef } from "react";
import {
  Box,
  VStack,
  Heading,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Button,
  Text,
  useToast,
  Link,
  InputGroup,
  InputRightElement,
  IconButton,
  HStack,
  Image,
  Divider,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

interface FormErrors {
  email?: string;
  password?: string;
}

const Login = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Refs for focus management
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Validation functions
  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password.trim()) {
      return 'Password is required';
    }
    return undefined;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error when user types
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleBlur = (field: keyof FormErrors) => {
    const value = formData[field as keyof typeof formData];
    let error: string | undefined;

    switch (field) {
      case 'email':
        error = validateEmail(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
    }

    if (error) {
      setErrors(prev => ({
        ...prev,
        [field]: error,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    newErrors.email = validateEmail(formData.email);
    newErrors.password = validatePassword(formData.password);
    
    setErrors(newErrors);
    
    // Focus on first invalid field
    if (newErrors.email) {
      emailRef.current?.focus();
      return false;
    }
    if (newErrors.password) {
      passwordRef.current?.focus();
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
   

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}auth/login`, formData);


      // store token + user
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      setUser(res.data.user);

      toast({
        title: "Login Successful",
        description: `Welcome back, ${res.data.user.name}!`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/");
    } catch (err: any) {
      toast({
        title: "Login Failed",
        description: err.response?.data?.message || "Invalid credentials",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box minH="100vh" bgGradient="linear(to-br, primary.50, white 60%)" display="flex" alignItems="center" justifyContent="center" p={4}>
      <Box maxW="400px" w="full" bg="white" borderRadius="2xl" boxShadow="2xl" p={8} borderWidth={1} borderColor="primary.100">
        <VStack spacing={6} align="stretch">
          {/* Logo & Title */}
          <VStack spacing={2} textAlign="center">
            <Image src="/favicon.png" alt="CMS Logo" boxSize="48px" mx="auto" />
            <Heading size="lg" color="primary.700">Client Management System</Heading>
            <Text color="gray.500" fontSize="md">Welcome Back! Please sign in to continue.</Text>
          </VStack>
          <Divider />
          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={!!errors.email}>
                <FormLabel>Email</FormLabel>
                <Input
                  id="email"
                  ref={emailRef}
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={() => handleBlur('email')}
                  size="lg"
                  bg="primary.50"
                  borderColor={errors.email ? 'red.500' : 'primary.200'}
                  _focus={{ 
                    borderColor: errors.email ? 'red.500' : 'primary.400', 
                    boxShadow: errors.email ? '0 0 0 2px #E53E3E' : '0 0 0 2px #7B4BC1' 
                  }}
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.password}>
                <FormLabel>Password</FormLabel>
                <InputGroup size="lg">
                  <Input
                    id="password"
                    ref={passwordRef}
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={() => handleBlur('password')}
                    bg="primary.50"
                    borderColor={errors.password ? 'red.500' : 'primary.200'}
                    _focus={{ 
                      borderColor: errors.password ? 'red.500' : 'primary.400', 
                      boxShadow: errors.password ? '0 0 0 2px #E53E3E' : '0 0 0 2px #7B4BC1' 
                    }}
                  />
                  <InputRightElement>
                    <IconButton
                      variant="ghost"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.password}</FormErrorMessage>
              </FormControl>
              <Button
                type="submit"
                colorScheme="primary"
                size="lg"
                width="full"
                isLoading={loading}
                fontWeight="bold"
                boxShadow="md"
              >
                Login
              </Button>
              
              {/* Forgot Password Link */}
              <Text fontSize="sm" textAlign="center">
                <Link 
                  color="primary.500" 
                  fontWeight="bold" 
                  onClick={() => navigate("/forgot-password")}
                  _hover={{ textDecoration: "underline" }}
                >
                  Forgot your password?
                </Link>
              </Text>
              
              <Text fontSize="sm" textAlign="center">
                Don't have an account?{' '}
                <Link color="primary.500" fontWeight="bold" onClick={() => navigate("/signup")}>Sign up</Link>
              </Text>
            </VStack>
          </form>
        </VStack>
      </Box>
    </Box>
  );
};

export default Login;


